import i18n from '@/utils/i18n';
import React from 'react';
import { BORDER } from '@/styles';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_CURRENCY_CODE, formatCurrency } from '@/utils/money';
import { formatDate } from '@/utils/date';
import { getPackDisplayString } from '@/utils/item';
import { isPastDate } from '@/utils/order-helpers';
import { ItemListOption } from '@/contexts/item-context-types';
import {
  LastPurchaseInfoType,
  useVariantPackInfoContext,
} from '@/contexts/variant-pack-info-context';
import {
  OrderableItemListItemImage,
  STANDARD_IMAGE_SIZE,
} from '@/components/orders/orderable-item-list-item-image';
import { PackUnit } from '@/enums';
import { QuantitySelector } from '@/components/quantity-selector';
import { Typography } from '@usepepper/ui/atoms/typography';
import {
  UpdateQuantityCallbackType,
  UpdateQuantityData,
} from '@/contexts/new-order-actions-context-types';
import { useDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { useItemDisplayOptions } from '@/contexts/new-order-context';
import { useItemVisibleQuantity } from '@/stores/new-order-quantity-store';
import { VariantPackEntry } from '@/adapters/offline-mode';
import { View } from 'react-native';

export function getEntryDisplayName(entry: VariantPackEntry): string {
  return entry.translations?.display_name || entry.display_name;
}

export function getEntryDescription(entry: VariantPackEntry): string | null {
  return entry.translations?.description || entry.description;
}

function getEntryPackDisplayString(entry: VariantPackEntry): string {
  return getPackDisplayString({
    maxUnitSize: entry.pack_max_unit_size,
    minUnitSize: entry.pack_min_unit_size,
    unit: entry.pack_unit as PackUnit | null,
  });
}

export function entryToItemListOption(entry: VariantPackEntry): ItemListOption {
  return {
    metadata: {
      externalID: entry.external_item_id,
      itemUUID: entry.item_uuid,
      maximumQuantity: entry.order_max_quantity ?? null,
      minimumQuantity: entry.order_min_quantity ?? null,
      skus: [],
      variantPackUUID: entry.uuid,

      // these are to satisfy typescript, not actually needed to display item details sheet properly in scanner lists
      // eslint-disable-next-line sort-keys/sort-keys-fix
      availabilityStatus: null,
      availableQuantity: null,
      baseUnit: null,
      category: null,
      currencyCode: DEFAULT_CURRENCY_CODE,
      description: getEntryDescription(entry),
      displayName: getEntryDisplayName(entry),
      outOfStockUUIDs: new Set<string>(),
      packDisplayString: getEntryPackDisplayString(entry),
      packUnit: PackUnit.Case,
      photoURLs: entry.photo_url ? [entry.photo_url] : [],
      promotion: null,
      promotionPricePerUnitInMicros: null,
      unitCount: 1,
      upcList: entry.upc_list,
      variantPackMetadata: {},
    },
    title: getEntryDisplayName(entry),
    value: entry.uuid,
  } as ItemListOption;
}

function getEntryCode(entry: VariantPackEntry): string | null {
  if (entry.external_item_id) {
    return entry.external_item_id;
  }
  if (entry.upc_list && entry.upc_list.length > 0) {
    return entry.upc_list[0];
  }
  return null;
}

function getLastOrderLabel(
  lastPurchaseInfo: LastPurchaseInfoType | undefined,
): string | null {
  if (!lastPurchaseInfo) {
    return null;
  }

  // fulfillmentDate and orderDate already include invoice dates as fallbacks
  // when orders don't exist (handled by getVariantPackLastPurchaseInfo in the backend)
  const metricDate =
    lastPurchaseInfo.fulfillmentDate ?? lastPurchaseInfo.orderDate;
  if (!metricDate) {
    return null;
  }

  const formattedDate = formatDate(metricDate, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  // an in-transit order can carry a future fulfillment date; fall back to the
  // generic "arriving" copy (same as last-ordered-message-date.tsx) instead
  // of mislabeling it as a past "last order"
  if (!isPastDate(metricDate)) {
    return lastPurchaseInfo.quantity !== null && lastPurchaseInfo.quantity !== 0
      ? i18n.t('arriving_quantity_on_date', {
          date: formattedDate,
          quantity: lastPurchaseInfo.quantity,
        })
      : i18n.t('arriving_on_date', { date: formattedDate });
  }

  // quantity 0 means the only purchase history we have is an invoice
  // line-item fallback with no real quantity, same as noPurchaseHistory
  // in last-ordered-message-date.tsx
  return lastPurchaseInfo.quantity !== null && lastPurchaseInfo.quantity !== 0
    ? i18n.t('scan.last_order_quantity_on_date', {
        date: formattedDate,
        quantity: lastPurchaseInfo.quantity,
      })
    : i18n.t('scan.last_order_on_date', { date: formattedDate });
}

// split out so useVariantPackInfoContext is only subscribed to (and only
// re-renders on pricing/quantity changes) when the flag is on
const ScannedItemAdditionalInfo: React.FC<{ variantPackUUID: string }> = ({
  variantPackUUID,
}) => {
  const [styles, theme] = useStyles();
  const { getVariantPackInfo, lastPurchaseInfoMap } =
    useVariantPackInfoContext();
  const variantPackInfo = getVariantPackInfo(variantPackUUID);
  const lastOrderLabel = getLastOrderLabel(
    lastPurchaseInfoMap[variantPackUUID],
  );

  const showAvailableQuantity =
    typeof variantPackInfo?.availableQuantity === 'number';

  if (!showAvailableQuantity && !lastOrderLabel) {
    return null;
  }

  return (
    <View collapsable={false} style={styles.additionalInfoRow}>
      {typeof variantPackInfo?.availableQuantity === 'number' ? (
        <Typography
          color={theme.sk.color.text.success}
          numberOfLines={1}
          variant="paragraph-sm-600"
        >
          {i18n.t('in_stock', { quantity: variantPackInfo.availableQuantity })}
        </Typography>
      ) : null}
      {lastOrderLabel ? (
        <Typography
          color={theme.sk.color.text.tertiary}
          numberOfLines={1}
          variant="paragraph-sm"
        >
          {lastOrderLabel}
        </Typography>
      ) : null}
    </View>
  );
};

const ScannedItemPackPrice: React.FC<{ variantPackUUID: string }> = ({
  variantPackUUID,
}) => {
  const { getVariantPackInfo } = useVariantPackInfoContext();
  const { displayItemPrices } = useItemDisplayOptions();
  const variantPackInfo = getVariantPackInfo(variantPackUUID);

  if (
    !displayItemPrices ||
    typeof variantPackInfo?.priceInMicros !== 'number'
  ) {
    return null;
  }

  return (
    <Typography numberOfLines={1} variant="paragraph-md-600">
      {formatCurrency(
        variantPackInfo.unitCount * variantPackInfo.priceInMicros,
        variantPackInfo.currencyCode,
      )}
    </Typography>
  );
};

interface Props {
  additionalProductInfoEnabled: boolean;
  debouncedUpdateQuantity: UpdateQuantityCallbackType;
  entry: VariantPackEntry;
  hidePlaceholderImages: boolean;
  imagesEnabled: boolean;
  matchedCode?: string | null;
  onRemoveEntry?: (variantPackUUID: string) => void;
  onUpdateQuantity?: (
    entry: VariantPackEntry,
    updateQuantityData: UpdateQuantityData,
  ) => void;
  quantity: number;
  source: string;
}

type MinimalScannerListItemProps = Omit<
  Props,
  'hidePlaceholderImages' | 'imagesEnabled' | 'quantity'
>;

export const MinimalScannerVariantPackListItem: React.FC<
  MinimalScannerListItemProps
> = ({
  additionalProductInfoEnabled,
  debouncedUpdateQuantity,
  entry,
  matchedCode,
  onRemoveEntry,
  onUpdateQuantity,
  source,
}) => {
  const quantity = useItemVisibleQuantity(entry.uuid);
  const { hidePlaceholderImages } = useItemDisplayOptions();
  const imagesEnabled = useDynamicConfigFlag(
    'on_device_scanning_config',
    'local_scan_ux_images_enabled',
  );

  return (
    <MemoizedScannerVariantPackListItem
      additionalProductInfoEnabled={additionalProductInfoEnabled}
      debouncedUpdateQuantity={debouncedUpdateQuantity}
      entry={entry}
      hidePlaceholderImages={hidePlaceholderImages}
      imagesEnabled={imagesEnabled}
      matchedCode={matchedCode}
      onRemoveEntry={onRemoveEntry}
      onUpdateQuantity={onUpdateQuantity}
      quantity={quantity}
      source={source}
    />
  );
};

// wrapping this so items don't rerender when another item's quantity changes, which is common in scanner lists
const MemoizedScannerVariantPackListItem: React.FC<Props> = React.memo(
  ({
    additionalProductInfoEnabled,
    debouncedUpdateQuantity,
    entry,
    hidePlaceholderImages,
    imagesEnabled,
    matchedCode,
    onRemoveEntry,
    onUpdateQuantity,
    quantity,
    source,
  }) => {
    const [styles] = useStyles();
    const code = matchedCode || getEntryCode(entry);
    const displayName = getEntryDisplayName(entry);
    const description = getEntryDescription(entry);
    const photoURLs = entry.photo_url ? [entry.photo_url] : [];

    const showImage =
      imagesEnabled && (photoURLs.length > 0 || !hidePlaceholderImages);

    return (
      <View style={[styles.container]}>
        <View style={styles.headerRow}>
          {showImage ? (
            <View style={[BORDER.primary, styles.imageTile]}>
              <OrderableItemListItemImage
                alt={displayName}
                hidePlaceholderImages={hidePlaceholderImages}
                imageSize={STANDARD_IMAGE_SIZE}
                imageURLs={photoURLs}
              />
            </View>
          ) : null}
          <View style={styles.details}>
            <View collapsable={false} style={styles.headerText}>
              <Typography numberOfLines={1} variant="heading-sm">
                {displayName}
              </Typography>
              {description ? (
                <Typography numberOfLines={1} variant="paragraph-md">
                  {description}
                </Typography>
              ) : null}
            </View>

            {additionalProductInfoEnabled ? (
              <ScannedItemAdditionalInfo variantPackUUID={entry.uuid} />
            ) : null}
          </View>
        </View>

        <View style={[BORDER.primary, styles.packRow]}>
          <View style={styles.packInfo}>
            <Typography numberOfLines={1} variant="paragraph-md">
              <Typography textTransform="capitalize" variant="paragraph-md">
                {code
                  ? `${getEntryPackDisplayString(entry)} • `
                  : getEntryPackDisplayString(entry)}
              </Typography>
              {code ?? ''}
            </Typography>
            {additionalProductInfoEnabled ? (
              <ScannedItemPackPrice variantPackUUID={entry.uuid} />
            ) : null}
          </View>
          <QuantitySelector
            isFocused={false}
            maxQuantity={entry.order_max_quantity}
            minQuantity={entry.order_min_quantity}
            onChange={(previousQuantity, newQuantity) => {
              if (newQuantity === 0 && onRemoveEntry) {
                onRemoveEntry(entry.uuid);
              }

              const updateQuantityData: UpdateQuantityData = {
                eventPayloads: null,
                newQuantity,
                previousQuantity,
                variantPackDetails: {
                  itemDisplayName: displayName,
                  metadata: null,
                },
                variantPackUUID: entry.uuid,
              };

              debouncedUpdateQuantity(updateQuantityData, { source });
              onUpdateQuantity?.(entry, updateQuantityData);
            }}
            quantity={quantity}
          />
        </View>
      </View>
    );
  },
);

const useStyles = createStyles((theme) => ({
  additionalInfoRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.sk.spacing.spaceSmall,
  },
  container: {
    flex: 1,
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSMedium,
  },
  details: {
    flex: 1,
    gap: theme.sk.spacing.spaceSmall,
  },
  headerRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
  },
  headerText: {
    gap: theme.sk.spacing.spaceXsmall,
  },
  imageTile: {
    alignItems: 'center',
    height: STANDARD_IMAGE_SIZE,
    justifyContent: 'center',
    marginTop: theme.sk.spacing.spaceSmall,
    overflow: 'hidden',
    width: STANDARD_IMAGE_SIZE,
  },
  packInfo: {
    flex: 1,
    gap: theme.sk.spacing.spaceXsmall,
  },
  packRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: theme.sk.spacing.spaceSMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
}));
